import dayjs from 'dayjs';

/**
 * One contact's page (card 19), as plain data and pure functions, tested
 * without rendering: the shapes CRM_CONTACT_DETAIL and CRM_CONTACT_TIMELINE
 * return, and every piece of wording the page derives from them.
 */

export interface ContactTag {
  tagId: number;
  name: string;
  category: string | null;
  color: string | null;
  isSystem: boolean;
  addedAt: string;
  source: string;
}

export interface ContactListMembership {
  segmentId: number;
  name: string;
  source: string;
  addedAt: string;
}

/** A membership the person holds (card 20). */
export interface ContactMembership {
  id: number;
  planName: string;
  status: string;
  startedAt: string | null;
  /** YYYY-MM-DD. */
  expiresOn: string | null;
  source: string;
}

export interface ContactAccount {
  id: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  level: string | null;
  totalPoints: number | null;
  role: string;
  isDeleted: boolean;
  isBanned: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  referralCount: number;
  recommendationCount: number;
}

/** How a do-not-call or do-not-contact came to be set (card 20.5): what the banner says. */
export interface FlagOrigin {
  how: 'CALL' | 'BATCH' | 'UNDO' | 'NATIONBUILDER' | 'MERGE' | 'UNRECORDED';
  at: string | null;
  note: string | null;
  callId: number | null;
  batchId: number | null;
  /** People the batch changed: 1 when set on this person alone. */
  batchSize: number | null;
  /** MERGE (card 28): the NationBuilder record merged into this contact, whose flag it was. */
  mergedContactId: number | null;
  admin: { id: number; firstName: string | null; lastName: string | null } | null;
}

/** The person's open follow-up (card 16; shown over their page since card 21). */
export interface OpenFollowUp {
  /** The call that set it. */
  id: number;
  occurredAt: string;
  followUpAt: string;
  followUpDue: boolean;
  outcome: string;
  notes: string | null;
  admin: { id: number; firstName: string | null; lastName: string | null } | null;
}

export interface ContactDetail {
  id: number;
  userId: number | null;
  nbSignupId: number | null;
  source: string;
  prefix: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  fullName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  emailOptIn: boolean;
  emailUnsubAt: string | null;
  smsOptIn: boolean;
  doNotCall: boolean;
  doNotContact: boolean;
  /** Card 20.5: null unless doNotCall. */
  doNotCallSet: FlagOrigin | null;
  /** Card 20.5: null unless doNotContact. */
  doNotContactSet: FlagOrigin | null;
  emailIsBad: boolean;
  emailBouncedAt: string | null;
  nbBannedAt: string | null;
  canEmail: boolean;
  canCall: boolean;
  canText: boolean;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  lastContactedAt: string | null;
  lastCalledAt: string | null;
  callCount: number;
  /** Card 21: the open follow-up, if any (card 16: at most one). */
  followUp: OpenFollowUp | null;
  supportLevel: number | null;
  lifecycleStage: string | null;
  matchMethod: string | null;
  matchConfidence: number | null;
  isPossibleDup: boolean;
  mergedIntoId: number | null;
  nbCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  joinedAt: string;
  engagement: {
    emailsSent: number;
    emailsOpened: number;
    emailsClicked: number;
    emailsBounced: number;
    lastSentAt: string | null;
    lastOpenedAt: string | null;
    lastClickedAt: string | null;
    engagementRate: number | null;
  } | null;
  emails: Array<{ id: number; email: string; isPrimary: boolean; isBad: boolean; isInvalid: boolean; bouncedAt: string | null }>;
  phones: Array<{
    id: number;
    type: string | null;
    e164: string | null;
    rawDisplay: string | null;
    isPrimary: boolean;
    isBad: boolean;
  }>;
  addresses: Array<{
    id: number;
    roles: Array<string>;
    isPrimary: boolean;
    address1: string | null;
    address2: string | null;
    address3: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    countryCode: string | null;
  }>;
  tags: Array<ContactTag>;
  lists: Array<ContactListMembership>;
  memberships: Array<ContactMembership>;
  notes: Array<{
    id: number;
    body: string;
    source: string;
    createdAt: string;
    /** Card 21.5. */
    updatedAt: string | null;
    /** Card 21.5: this admin wrote it here, so may change or delete it. */
    editable: boolean;
    admin: { id: number; firstName: string | null; lastName: string | null } | null;
  }>;
  account: ContactAccount | null;
  owner: { id: number; firstName: string | null; lastName: string | null } | null;
}

export type TimelineKind = 'CALL' | 'SMS' | 'NOTIFICATION' | 'APP' | 'NATIONBUILDER' | 'TAG' | 'LIST' | 'CHANGE';

interface Person {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export interface TimelineEntry {
  id: string;
  kind: TimelineKind;
  event: string;
  occurredAt: string;
  title: string;
  body: string | null;
  contactId: number | null;
  call: {
    callId: number;
    outcome: string;
    method: string | null;
    direction: string | null;
    durationSec: number | null;
    followUpAt: string | null;
    /** Card 20.5: they asked not to be called on this call. */
    setDoNotCall: boolean;
    /** Card 21.7: a link to the call's recording. */
    recordingUrl?: string | null;
    admin: Person | null;
    author: { id: number; fullName: string | null } | null;
  } | null;
  sms: { direction: string; toNumber: string | null; failedAt: string | null; errorMessage: string | null } | null;
  notification: { status: string | null; reason: string | null } | null;
  app: {
    businessName: string | null;
    withdrawnAt: string | null;
    referredUserName: string | null;
    achievementTitle: string | null;
    points: number | null;
  } | null;
  nationBuilder: { type: string; isPrivate: boolean } | null;
  tag: { tagId: number; name: string; color: string | null; source: string; admin: Person | null } | null;
  /** Card 20: put on a list or taken off one. */
  list: { segmentId: number; name: string; admin: Person | null } | null;
  /** Card 20: a field changed by a batch update, or put back by its undo. */
  change: {
    field: string;
    label: string;
    from: string | null;
    to: string | null;
    batchId: number | null;
    undo: boolean;
    admin: Person | null;
  } | null;
}

export interface TimelinePage {
  items: Array<TimelineEntry>;
  hasNextPage: boolean;
  endCursor: string | null;
  contactIds: Array<number>;
  counts?: Array<{ kind: TimelineKind; count: number }>;
}

/* ------------------------------------------------------------------ *
 * Identity
 * ------------------------------------------------------------------ */

export const displayName = (contact: Pick<ContactDetail, 'id' | 'fullName' | 'primaryEmail'>) =>
  contact.fullName?.trim() || contact.primaryEmail || `Contact #${contact.id}`;

export const personName = (person: Person | null | undefined) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ').trim() : '';

export const formatDay = (value: string | null | undefined) => (value ? dayjs(value).format('D MMM YYYY') : '');

export const formatMoment = (value: string | null | undefined) => (value ? dayjs(value).format('D MMM YYYY, HH:mm') : '');

/** A phone as stored: E.164 when it could be normalised, else NationBuilder's own text, which cannot be dialled. */
export const phoneText = (phone: { e164: string | null; rawDisplay: string | null }) => phone.e164 ?? phone.rawDisplay ?? '';

export const addressLines = (address: ContactDetail['addresses'][number]): Array<string> =>
  [
    address.address1,
    address.address2,
    address.address3,
    [address.city, address.state, address.zip].filter(Boolean).join(', '),
    address.countryCode,
  ].filter((line): line is string => Boolean(line && line.trim()));

export const placeOf = (contact: Pick<ContactDetail, 'city' | 'region' | 'countryCode'>) =>
  [contact.city, contact.region, contact.countryCode].filter(Boolean).join(', ');

export const SOURCE_WORDS: Record<string, string> = {
  NATIONBUILDER: 'NationBuilder',
  APP: 'the app',
  IMPORT: 'an import',
  MANUAL: 'an admin',
  WORKFLOW: 'a workflow',
};

export const MATCH_WORDS: Record<string, string> = {
  EXTERNAL_ID: 'the app id NationBuilder stored',
  NB_USER_TABLE: "NationBuilder's own link to the app",
  EMAIL: 'email address',
  PHONE: 'phone number',
  NAME_ADDRESS: 'name and address',
  NONE: 'no match',
};

/** How the migration linked this record to the app account, and how sure it was. */
export const describeMatch = (contact: Pick<ContactDetail, 'matchMethod' | 'matchConfidence'>) => {
  if (!contact.matchMethod || contact.matchMethod === 'NONE') return '';
  const how = MATCH_WORDS[contact.matchMethod] ?? contact.matchMethod.toLowerCase();
  return contact.matchConfidence == null ? `Matched by ${how}` : `Matched by ${how}, ${contact.matchConfidence}% sure`;
};

/* ------------------------------------------------------------------ *
 * Consent: the server's three gates, and why each is closed
 * (crm-contact-query.ts CAN_EMAIL, CAN_CALL, CAN_TEXT)
 * ------------------------------------------------------------------ */

export interface Gate {
  key: 'canEmail' | 'canCall' | 'canText';
  label: string;
  allowed: boolean;
  /** Why it is closed, every reason that applies; empty when open. */
  reasons: Array<string>;
}

export const consentGates = (contact: ContactDetail): Array<Gate> => {
  const dnc = contact.doNotContact ? ['Do not contact'] : [];
  const emailReasons = [
    ...dnc,
    ...(contact.emailOptIn
      ? []
      : [contact.emailUnsubAt ? `Unsubscribed ${formatDay(contact.emailUnsubAt)}` : 'Not opted in to email']),
    ...(contact.emailIsBad
      ? [contact.emailBouncedAt ? `Address bounced ${formatDay(contact.emailBouncedAt)}` : 'Address marked bad']
      : []),
    ...(contact.primaryEmail ? [] : ['No email address']),
  ];
  // Card 21.5: a main number marked bad closes calls and texts, as the API's gates do.
  const mainBad = contact.primaryPhone != null && contact.phones.some((p) => p.e164 === contact.primaryPhone && p.isBad);
  const phone = !contact.primaryPhone ? ['No phone number'] : mainBad ? ['Their number is marked bad'] : [];
  return [
    { key: 'canEmail', label: 'Email', allowed: contact.canEmail, reasons: contact.canEmail ? [] : emailReasons },
    {
      key: 'canCall',
      label: 'Call',
      allowed: contact.canCall,
      reasons: contact.canCall ? [] : [...dnc, ...(contact.doNotCall ? ['Do not call'] : []), ...phone],
    },
    {
      key: 'canText',
      label: 'Text',
      allowed: contact.canText,
      reasons: contact.canText ? [] : [...dnc, ...(contact.smsOptIn ? [] : ['Not opted in to texts']), ...phone],
    },
  ];
};

/* ------------------------------------------------------------------ *
 * Do not call, do not contact (card 20.5): the banner, not a checkbox
 * ------------------------------------------------------------------ */

export type FlagField = 'doNotCall' | 'doNotContact';

export const FLAG_WORDS: Record<FlagField, { title: string; means: string }> = {
  doNotCall: {
    title: 'Do not call',
    means: "Don't phone them. An outbound call to them cannot be logged, and the call queue never serves them.",
  },
  doNotContact: {
    title: 'Do not contact',
    means: 'No calls, texts or emails of any kind. Every send and call from the CRM leaves them out.',
  },
};

const quoted = (note: string | null) => (note ? `: "${note}"` : '');

/** A full stop, unless the quoted note already ends a sentence: not `"Stop calling."."`. */
const stop = (sentence: string) => (/[.!?]"$/.test(sentence) ? sentence : `${sentence}.`);

/**
 * Who set the flag, when and why, in one sentence: "They asked on a call on
 * 11 Oct 2026, logged by ILBP Admin: "Please stop calling me"."
 */
export const describeFlagOrigin = (origin: FlagOrigin | null): string => {
  if (!origin) return 'How it was set is not known.';
  const who = personName(origin.admin);
  const when = formatDay(origin.at);
  switch (origin.how) {
    case 'CALL':
      return stop(`They asked on a call${when ? ` on ${when}` : ''}${who ? `, logged by ${who}` : ''}${quoted(origin.note)}`);
    case 'BATCH': {
      const many = (origin.batchSize ?? 1) > 1;
      const by = who ? ` by ${who}` : '';
      const where = many ? ` in batch update #${origin.batchId}, with ${(origin.batchSize ?? 0).toLocaleString('en-US')} people` : '';
      return stop(`Set${by}${when ? ` on ${when}` : ''}${where}${quoted(origin.note)}`);
    }
    case 'UNDO':
      return stop(`Put back${who ? ` by ${who}` : ''}${when ? ` on ${when}` : ''}, undoing batch update #${origin.batchId}${quoted(origin.note)}`);
    case 'NATIONBUILDER':
      return 'It came from NationBuilder, which kept no date or reason.';
    case 'MERGE':
      return stop(
        `It came with record #${origin.mergedContactId}, merged into this contact${when ? ` on ${when}` : ''}${who ? ` when ${who} approved the match` : ''}`,
      );
    default:
      return 'How it was set is not recorded.';
  }
};

/** The flags to show as banners: do not contact covers calls, so a person marked both shows one. */
export const shownFlags = (contact: Pick<ContactDetail, 'doNotCall' | 'doNotContact'>): Array<FlagField> =>
  contact.doNotContact ? ['doNotContact'] : contact.doNotCall ? ['doNotCall'] : [];

/** What the dialog sends: a one-person batch update, so the change is recorded and can be undone (card 20). */
export const flagChange = (field: FlagField, value: boolean, note: string) => {
  const trimmed = note.trim();
  return { fields: { [field]: value }, ...(trimmed ? { note: trimmed } : {}) };
};

/** Lifting a flag opens calls (or all contact): the API, like a batch, wants to know how they agreed. */
export const flagNeedsNote = (value: boolean) => !value;

/* ------------------------------------------------------------------ *
 * The timeline (card 14)
 * ------------------------------------------------------------------ */

/** The chips over the feed, in this order. */
export const TIMELINE_KINDS: Array<{ kind: TimelineKind; label: string }> = [
  { kind: 'CALL', label: 'Calls' },
  { kind: 'SMS', label: 'Texts' },
  { kind: 'TAG', label: 'Tags' },
  { kind: 'LIST', label: 'Lists' },
  { kind: 'CHANGE', label: 'Changes' },
  { kind: 'APP', label: 'App activity' },
  { kind: 'NOTIFICATION', label: 'Notifications' },
  { kind: 'NATIONBUILDER', label: 'NationBuilder' },
];

/** Entries per page. */
export const TIMELINE_PAGE = 25;

const lower = (value: string | null | undefined) => (value ? value.toLowerCase().replace(/_/g, ' ') : '');

const minutes = (seconds: number) => (seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`);

/**
 * The small facts under an entry's title: who, how, how long. Only what the
 * title does not already say.
 */
export const entryFacts = (entry: TimelineEntry): Array<string> => {
  switch (entry.kind) {
    case 'CALL': {
      const call = entry.call;
      if (!call) return [];
      const caller = personName(call.admin) || call.author?.fullName || '';
      return [
        call.setDoNotCall ? 'asked not to be called' : '',
        caller && `by ${caller}`,
        lower(call.method),
        lower(call.direction),
        call.durationSec ? minutes(call.durationSec) : '',
        call.followUpAt ? `follow up ${formatDay(call.followUpAt)}` : '',
      ].filter(Boolean);
    }
    case 'SMS': {
      const sms = entry.sms;
      if (!sms) return [];
      return [
        sms.toNumber ? `to ${sms.toNumber}` : '',
        sms.failedAt ? `failed${sms.errorMessage ? `: ${sms.errorMessage}` : ''}` : '',
      ].filter(Boolean);
    }
    case 'TAG': {
      const tag = entry.tag;
      if (!tag) return [];
      const who = personName(tag.admin);
      if (who) return [`by ${who}`];
      return tag.source === 'MANUAL' ? [] : [`from ${SOURCE_WORDS[tag.source] ?? lower(tag.source)}`];
    }
    case 'LIST': {
      const who = personName(entry.list?.admin);
      return who ? [`by ${who}`] : [];
    }
    case 'CHANGE': {
      const change = entry.change;
      if (!change) return [];
      const who = personName(change.admin);
      return [
        who && `by ${who}`,
        // A removal's title names the old value already (card 21.5); a long note is cut short.
        change.from != null && change.to != null && change.field !== 'membership'
          ? `was ${change.from.length > 80 ? `${change.from.slice(0, 79)}…` : change.from}`
          : '',
        change.batchId ? `${change.undo ? 'undoing ' : ''}batch #${change.batchId}` : '',
      ].filter(Boolean);
    }
    case 'APP': {
      const app = entry.app;
      if (!app) return [];
      return [app.withdrawnAt ? `withdrawn ${formatDay(app.withdrawnAt)}` : '', app.points ? `${app.points} points` : ''].filter(
        Boolean,
      );
    }
    case 'NOTIFICATION':
      return [lower(entry.notification?.status), entry.notification?.reason ?? ''].filter(Boolean);
    case 'NATIONBUILDER':
      return entry.nationBuilder?.isPrivate ? ['private'] : [];
    default:
      return [];
  }
};

/** "Today", "Yesterday", or the date: the heading over each day's entries. */
export const dayHeading = (value: string, now = dayjs()) => {
  const day = dayjs(value);
  if (day.isSame(now, 'day')) return 'Today';
  if (day.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  return day.isSame(now, 'year') ? day.format('ddd D MMM') : day.format('D MMM YYYY');
};
